/* Confirm your email address.
 *
 * The server has always been able to send a six-digit code and check it, and
 * `REQUIRE_VERIFIED_EMAIL` refuses an unconfirmed account everything but a few
 * routes. The React client had no way to enter the code, so switching that on
 * would have locked every unconfirmed account out, the founder's included,
 * with nothing on screen to do about it.
 *
 * Shown above every screen until the address is confirmed. It sends nothing
 * until asked: an email nobody requested is how codes end up in spam folders.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { post, errorMessage } from '../api';
import { useSession } from '../session';
import Icon from './Icon';

const LATER = 'amon_verify_later';

export default function VerifyBanner() {
  const { user, refresh } = useSession();
  const [sent, setSent] = useState(false);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* "Later" lasts the session, not for ever: an unconfirmed address is worth
     being reminded of tomorrow, not every time a screen changes today. */
  const [later, setLater] = useState(() => {
    try { return sessionStorage.getItem(LATER) === '1'; } catch { return false; }
  });

  if (!user || user.verified || later) return null;

  const hide = () => {
    try { sessionStorage.setItem(LATER, '1'); } catch { /* private mode */ }
    setLater(true);
  };

  async function send() {
    setBusy(true); setError(null);
    const r = await post<{ sent?: boolean; alreadyVerified?: boolean }>('/api/auth/resend-verification');
    setBusy(false);
    if (!r.ok) { setError(errorMessage(r, 'Could not send the code.')); return; }
    if (r.alreadyVerified) { await refresh(); return; }
    setSent(true);
  }

  async function confirm(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    const r = await post('/api/auth/verify', { code: code.trim() });
    setBusy(false);
    if (!r.ok) {
      setError((r as { error?: string }).error === 'bad_code'
        ? 'That code does not match. Check the latest email, or send a new one.'
        : errorMessage(r, 'Could not confirm that.'));
      return;
    }
    await refresh();
  }

  return (
    <div className="notice verify-banner" role="region" aria-label="Confirm your email">
      <span className="notice-icon"><Icon name="send" size={18} /></span>
      <div>
        <p><strong>Confirm your email address</strong></p>
        <p className="muted small">
          {sent ? <>A six-digit code is on its way to <strong>{user.email}</strong>.</>
            : <>So reminders, resets and invitations reach you at <strong>{user.email}</strong>.</>}
        </p>
        {error && <p className="auth-err" role="alert">{error}</p>}
      </div>
      {sent ? (
        <form className="inline" onSubmit={confirm}>
          <input value={code} inputMode="numeric" autoComplete="one-time-code" maxLength={6}
                 placeholder="123456" aria-label="Six-digit code"
                 onChange={e => setCode(e.target.value.replace(/\D/g, ''))} />
          <button className="btn btn-primary btn-sm" type="submit" disabled={busy || code.length !== 6}>
            {busy ? 'Checking…' : 'Confirm'}
          </button>
          <button className="btn btn-ghost btn-sm" type="button" onClick={send} disabled={busy}>Send again</button>
        </form>
      ) : (
        <div className="inline">
          <button className="btn btn-primary btn-sm" onClick={send} disabled={busy}>
            {busy ? 'Sending…' : 'Send me a code'}
          </button>
          <button className="btn btn-ghost btn-icon" onClick={hide} aria-label="Later" title="Later">
            <Icon name="close" size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
